import React, { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import { tierProgressionNotificationMessages } from './LoyaltyTierProgressionNotificationMessages'

const messages = tierProgressionNotificationMessages

const MobileNotificationCard = styled.div`
  background: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${rem(8)};
  box-shadow: 
    0 ${rem(-1)} ${rem(5)} 0 rgba(0, 0, 0, 0.1),
    0 ${rem(-4)} ${rem(14)} 0 rgba(0, 0, 0, 0.05),
    0 ${rem(-8)} ${rem(24)} 0 rgba(0, 0, 0, 0.05),
    0 ${rem(-24)} ${rem(35)} 0 rgba(0, 0, 0, 0.05);
  padding: ${rem(12)} ${rem(12)};
  display: flex;
  align-items: center;
  gap: ${rem(12)};
`

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${rem(5)};
  min-width: 0;
`

const CloseButtonWrapper = styled.div`
  flex-shrink: 0;
  align-self: flex-start;
`

const RequirementsText = styled(BodyText)`
  color: ${props => props.theme.palette.neutral.default.one};
`

const BoldText = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.bold};
`

interface Props {
  nextTier: App.LoyaltyTier
  creditsRemaining: number
  nightsRemaining: number
  userName?: string
  onViewProgress?: () => void
  onClose?: () => void
}

function LoyaltyTierProgressionNotificationMobile({
  nextTier,
  creditsRemaining,
  nightsRemaining,
  userName,
  onViewProgress,
  onClose,
}: Props) {
  const intl = useIntl()
  const tierName = getLoyaltyTierDisplayName(nextTier)

  const hasCredits = creditsRemaining > 0
  const hasNights = nightsRemaining > 0

  const headerText = userName
    ? intl.formatMessage(messages.headerWithName, { userName, tierName })
    : intl.formatMessage(messages.headerDefault, { tierName })
  const viewProgressText = intl.formatMessage(messages.viewProgress)
  const closeAriaLabel = intl.formatMessage(messages.closeAriaLabel)

  const handleViewProgress = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    onViewProgress?.()
  }, [onViewProgress])

  return (
    <MobileNotificationCard>
      <LoyaltyTierIcon tier={nextTier} size={40} />

      <ContentSection>
        <BodyText variant="medium" weight="bold">
          {headerText}
        </BodyText>

        <RequirementsText variant="small">
          {hasCredits && hasNights && (
            <FormattedMessage
              id="LYgN9yCBjBRKeu-i2zFO"
              defaultMessage="You only need <bold>{creditsRemaining, plural, one {# Status Credit} other {# Status credits}} and {nightsRemaining, plural, one {# night} other {# nights}}</bold> to unlock {tierName} benefits."
              description="Mobile tier progression notification requiring both credits and nights"
              values={{
                creditsRemaining,
                nightsRemaining,
                tierName,
                bold: (chunks: React.ReactNode) => <BoldText>{chunks}</BoldText>,
              }}
            />
          )}
          {hasCredits && !hasNights && (
            <FormattedMessage
              id="iSZp6-ScQa8_swMD1WVO"
              defaultMessage="You only need <bold>{creditsRemaining, plural, one {# Status Credit} other {# Status credits}}</bold> to unlock {tierName} benefits."
              description="Mobile tier progression notification requiring credits only"
              values={{
                creditsRemaining,
                tierName,
                bold: (chunks: React.ReactNode) => <BoldText>{chunks}</BoldText>,
              }}
            />
          )}
          {!hasCredits && hasNights && (
            <FormattedMessage
              id="b50dDDQNrNiuUii94tP0"
              defaultMessage="You only need <bold>{nightsRemaining, plural, one {# night} other {# nights}}</bold> to unlock {tierName} benefits."
              description="Mobile tier progression notification requiring nights only"
              values={{
                nightsRemaining,
                tierName,
                bold: (chunks: React.ReactNode) => <BoldText>{chunks}</BoldText>,
              }}
            />
          )}
        </RequirementsText>
        <TextLink size="small" weight="regular" onClick={handleViewProgress}>
          {viewProgressText}
        </TextLink>
      </ContentSection>

      <CloseButtonWrapper>
        <IconButton
          kind="tertiary"
          size="small"
          onClick={onClose}
          aria-label={closeAriaLabel}
        >
          <LineTimesIcon colour="neutral-one" />
        </IconButton>
      </CloseButtonWrapper>
    </MobileNotificationCard>
  )
}

export default LoyaltyTierProgressionNotificationMobile
